import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import PublicPageLayout from '../components/PublicPageLayout';
import { ChevronRight, ArrowRight, BookOpen, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const categoryColors = {
  'Tax Tips': 'bg-emerald-50 text-emerald-700',
  'Technology': 'bg-blue-50 text-blue-700',
  'Business Guide': 'bg-purple-50 text-purple-700',
  'general': 'bg-slate-100 text-slate-600',
};

export default function BlogPage() {
  const { data: blogs = [], isLoading } = useQuery({
    queryKey: ['blogs'],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/api/blogs?status=published`);
      return data;
    },
  });

  return (
    <PublicPageLayout>
      <div data-testid="blog-page">
        {/* Hero */}
        <section className="bg-gradient-to-b from-slate-50 to-white py-16">
          <div className="max-w-4xl mx-auto px-6">
            <div className="flex items-center gap-2 text-sm text-slate-400 mb-8">
              <Link to="/" className="hover:text-slate-600 transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-slate-600 font-medium">Blog</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-emerald-100 p-2.5 rounded-xl">
                <BookOpen className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">ExpensIQ Blog</h1>
            </div>
            <p className="text-base text-slate-500 max-w-xl">
              Tips, guides, and insights for Australian businesses on expense management, tax deductions, and financial growth.
            </p>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : blogs.length === 0 ? (
              <div className="text-center py-20">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No posts yet</h3>
                <p className="text-sm text-slate-500">Check back soon for new content.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-6">
                {blogs.map((blog, i) => (
                  <Link
                    key={blog.id || blog.slug}
                    to={`/blog/${blog.slug}`}
                    className="group block bg-white rounded-2xl border border-slate-200/80 hover:border-emerald-300 hover:shadow-lg transition-all duration-300 overflow-hidden"
                    data-testid={`blog-card-${blog.slug}`}
                  >
                    {/* Color bar */}
                    <div className="h-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`px-2.5 py-1 rounded-md text-xs font-medium ${categoryColors[blog.category] || categoryColors.general}`}>
                          {blog.category}
                        </span>
                        {blog.created_at && (
                          <span className="flex items-center gap-1 text-xs text-slate-400">
                            <Calendar className="w-3 h-3" />
                            {new Date(blog.created_at).toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        )}
                      </div>
                      <h2 className="text-base font-bold text-slate-900 group-hover:text-emerald-600 transition-colors mb-2 line-clamp-2">
                        {blog.title}
                      </h2>
                      <p className="text-sm text-slate-500 line-clamp-3 mb-4">
                        {blog.excerpt || blog.content?.slice(0, 150)}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">By {blog.author}</span>
                        <span className="flex items-center gap-1 text-sm font-medium text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity">
                          Read more <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </PublicPageLayout>
  );
}
